import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";

import {
  Button,
  CssBaseline,
  TextField,
  Grid,
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Link,
  Chip,
  useMediaQuery
} from '@material-ui/core';

import { Visibility, VisibilityOff } from '@material-ui/icons';

import { makeStyles, useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhiteLabel } from "../../context/WhiteLabel/WhiteLabelContext";
import api from "../../services/api";
// fuente migrará a WhiteLabelConfig (feature Marca Blanca)
import {
  LOGIN_PANEL_SECTIONS,
  LOGIN_META_BADGE,
} from "../../config/loginPanelSections";

const ROTATE_MS = 6000;

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    width: "100%",
    overflowX: "hidden",
  },
  // Left column — form (auth flow INTACTO)
  formPane: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.default,
  },
  formInner: {
    width: "100%",
    maxWidth: 400,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    width: 260,
    maxWidth: "80%",
    marginBottom: theme.spacing(3),
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    padding: theme.spacing(1.4, 0),
    fontWeight: 600,
    fontSize: "1rem",
  },
  // Right column — brand panel (WAIA identity: WhatsApp-adjacent green→teal→blue)
  // V2-C: profundidad SOLO con CSS (base gradient + radiales + textura de puntos).
  brandPane: {
    position: "relative",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(6),
    color: "#ffffff",
    overflow: "hidden",
    backgroundColor: "#128C7E",
    backgroundImage: [
      "radial-gradient(circle at 18% 22%, rgba(37,211,102,0.55) 0%, rgba(37,211,102,0) 42%)",
      "radial-gradient(circle at 82% 78%, rgba(37,118,210,0.55) 0%, rgba(37,118,210,0) 46%)",
      "radial-gradient(circle at 60% 8%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 30%)",
      "linear-gradient(150deg, #25D366 0%, #128C7E 50%, #2576d2 100%)",
    ].join(","),
  },
  // Textura de puntos sutil por encima del gradiente (capa aparte para no floten líneas en streaming)
  brandTexture: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)",
    backgroundSize: "22px 22px",
    opacity: 0.35,
    pointerEvents: "none",
    zIndex: 0,
  },
  brandGlow: {
    position: "absolute",
    top: "-20%",
    right: "-10%",
    width: 480,
    height: 480,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.12)",
    filter: "blur(30px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  brandContent: {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  // Nav de labels — V2: clickeable + pausa rotación. <button> por accesibilidad.
  panelNav: {
    display: "flex",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
    flexWrap: "wrap",
  },
  navLabel: {
    // sin text-transform: los strings i18n ya vienen con el casing correcto
    // (arregla "TurnosIA" que se veía "TURNOSIA")
    font: "inherit",
    fontSize: "0.82rem",
    fontWeight: 600,
    letterSpacing: 1,
    color: "#ffffff",
    opacity: 0.55,
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    paddingBottom: theme.spacing(0.5),
    cursor: "pointer",
    transition: "opacity .2s ease, border-color .2s ease",
    "&:hover, &:focus-visible": {
      opacity: 0.85,
      outline: "none",
    },
  },
  navLabelActive: {
    opacity: 1,
    borderBottomColor: "#ffffff",
  },
  // Hero — se remonta por key(activeIndex) → replay del fade
  hero: {
    animation: "$heroIn .45s ease",
  },
  heroIconWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 56,
    height: 56,
    borderRadius: 16,
    background: "rgba(255,255,255,0.16)",
    marginBottom: theme.spacing(2),
  },
  heroIcon: {
    fontSize: 32,
  },
  heroTitle: {
    fontWeight: 800,
    letterSpacing: "-0.5px",
    lineHeight: 1.15,
    marginBottom: theme.spacing(3),
    textShadow: "0 1px 12px rgba(0,0,0,0.18)",
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(5),
  },
  chip: {
    background: "rgba(255,255,255,0.20)",
    color: "#ffffff",
    fontWeight: 500,
  },
  "@keyframes heroIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  // Teasers (secciones NO activas, compactas) — clickeables igual que la nav
  teasersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  teaser: {
    flex: "1 1 200px",
    minWidth: 200,
    background: "rgba(255,255,255,0.10)",
    borderRadius: 12,
    padding: theme.spacing(2),
    border: "1px solid rgba(255,255,255,0.06)",
    color: "#ffffff",
    textAlign: "left",
    cursor: "pointer",
    transition: "background .2s ease, transform .2s ease",
    "&:hover, &:focus-visible": {
      background: "rgba(255,255,255,0.16)",
      outline: "none",
      transform: "translateY(-2px)",
    },
  },
  teaserHead: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  teaserIcon: {
    fontSize: 22,
  },
  teaserTitle: {
    fontWeight: 700,
    fontSize: "0.95rem",
  },
  teaserChips: {
    fontSize: "0.8rem",
    opacity: 0.9,
    lineHeight: 1.5,
  },
  // Meta badge slot — bottom of brand panel (fijo, texto exacto)
  metaBadgeWrap: {
    position: "absolute",
    bottom: theme.spacing(4),
    left: 0,
    right: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    zIndex: 1,
  },
  metaBadgeIcon: {
    fontSize: 20,
    opacity: 0.9,
  },
  metaBadgeText: {
    fontSize: "0.78rem",
    letterSpacing: 0.5,
    opacity: 0.9,
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSelfRegistration, setAllowSelfRegistration] = useState(false);

  const { handleLogin } = useContext(AuthContext);
  const { whiteLabel: whiteLabelConfig, isHomeDomain, isLoading: wlLoading } = useWhiteLabel();

  useEffect(() => {
    api.get("/public-config").then(r => {
      setAllowSelfRegistration(r.data.allowSelfRegistration === true);
    }).catch(() => {});
  }, []);

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  const brandName = whiteLabelConfig?.name || "WAIA";
  const logoSrc =
    whiteLabelConfig?.loginLogoUrl ||
    whiteLabelConfig?.logoUrl ||
    "/logo-login.svg";

  // Panel derecho branded SOLO en dominio HOME (resuelto sync → sin flash).
  // Marca blanca → login CENTRADO en una columna (sin panel vacío roto).
  const showBrandPanel = !isMobile && isHomeDomain;
  const formMd = showBrandPanel ? 4 : 12;

  // Logo del form: mostrar el de WAIA sólo en HOME; en un dominio WL esperar a
  // que resuelva el fetch (evita un frame con el logo WAIA en el revendedor).
  const showFormLogo = isHomeDomain || !wlLoading;

  // ===== V2-A: rotación + nav interactiva =====
  const [activeIndex, setActiveIndex] = useState(0);
  const [clickPaused, setClickPaused] = useState(false); // click = pausa permanente
  const [hovering, setHovering] = useState(false); // hover/focus = pausa transitoria

  const rotationOn =
    showBrandPanel && !prefersReducedMotion && !clickPaused && !hovering;

  useEffect(() => {
    if (!rotationOn) return undefined;
    const id = setInterval(() => {
      setActiveIndex((i) => (i + 1) % LOGIN_PANEL_SECTIONS.length);
    }, ROTATE_MS);
    return () => clearInterval(id);
  }, [rotationOn]);

  const selectSection = useCallback((idx) => {
    setActiveIndex(idx);
    setClickPaused(true);
  }, []);

  const pauseHover = useCallback(() => setHovering(true), []);
  const resumeHover = useCallback(() => setHovering(false), []);

  const activeSection = LOGIN_PANEL_SECTIONS[activeIndex];
  const HeroIcon = activeSection.icon;
  const MetaBadgeIcon = LOGIN_META_BADGE.icon;

  const renderBrandPanel = () => (
    <Grid
      item
      xs={12}
      md={8}
      className={classes.brandPane}
      onMouseEnter={pauseHover}
      onMouseLeave={resumeHover}
    >
      <div className={classes.brandTexture} />
      <div className={classes.brandGlow} />
      <div className={classes.brandContent}>
        {/* Nav de labels — clickeable, pausa la rotación */}
        <div className={classes.panelNav}>
          {LOGIN_PANEL_SECTIONS.map((s, idx) => (
            <button
              type="button"
              key={s.id}
              onClick={() => selectSection(idx)}
              onFocus={pauseHover}
              onBlur={resumeHover}
              className={`${classes.navLabel} ${idx === activeIndex ? classes.navLabelActive : ""}`}
            >
              {i18n.t(s.titleKey.replace(".title", ".nav"))}
            </button>
          ))}
        </div>

        {/* Hero (sección activa) — key fuerza replay del fade al rotar/clickear */}
        <div
          className={prefersReducedMotion ? undefined : classes.hero}
          key={activeIndex}
        >
          <div className={classes.heroIconWrap}>
            <HeroIcon className={classes.heroIcon} />
          </div>
          <Typography variant="h3" className={classes.heroTitle}>
            {i18n.t(activeSection.titleKey)}
          </Typography>
          <div className={classes.chipsRow}>
            {activeSection.chipsKeys.map((k) => (
              <Chip key={k} label={i18n.t(k)} className={classes.chip} size="small" />
            ))}
          </div>
        </div>

        {/* Teasers (secciones NO activas) — click salta a esa sección */}
        <div className={classes.teasersRow}>
          {LOGIN_PANEL_SECTIONS.map((s, idx) => {
            if (idx === activeIndex) return null;
            const TeaserIcon = s.icon;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => selectSection(idx)}
                onFocus={pauseHover}
                onBlur={resumeHover}
                className={classes.teaser}
              >
                <div className={classes.teaserHead}>
                  <TeaserIcon className={classes.teaserIcon} />
                  <span className={classes.teaserTitle}>
                    {i18n.t(s.titleKey)}
                  </span>
                </div>
                <div className={classes.teaserChips}>
                  {s.chipsKeys.map((k) => i18n.t(k)).join(" · ")}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Badge Meta fijo — texto exacto, no rota */}
      <div className={classes.metaBadgeWrap}>
        <MetaBadgeIcon className={classes.metaBadgeIcon} />
        <span className={classes.metaBadgeText}>
          {i18n.t(LOGIN_META_BADGE.textKey)}
        </span>
      </div>
    </Grid>
  );

  return (
    <>
      <CssBaseline />
      <Grid container className={classes.root}>
        {/* ===== Form (auth intacto) =====
            HOME desktop → columna izquierda (md=4) + panel branded.
            Marca blanca / mobile → login CENTRADO en una columna (md=12), sin panel.
            // interino: reemplazar por panel config-driven (feature Marca Blanca) */}
        <Grid item xs={12} md={formMd} className={classes.formPane}>
          <div className={classes.formInner}>
            {/* visibility (no display) → reserva el alto y evita salto de layout;
                oculto durante la carga en dominio WL para no mostrar el logo WAIA.
                En marca blanca el logo del revendedor va arriba del form centrado. */}
            <img
              src={logoSrc}
              alt={brandName}
              className={classes.logo}
              style={{ visibility: showFormLogo ? "visible" : "hidden" }}
            />
            <Typography component="h1" variant="h5" style={{ fontWeight: 600 }}>
              {i18n.t("login.title")}
            </Typography>
            <form className={classes.form} noValidate onSubmit={handlSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword((e) => !e)}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {i18n.t("login.buttons.submit")}
              </Button>
              {allowSelfRegistration && (
                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/signup"
                    >
                      {i18n.t("login.buttons.register")}
                    </Link>
                  </Grid>
                </Grid>
              )}
            </form>
          </div>
        </Grid>

        {/* ===== Right: brand panel — SOLO home desktop (sin flash WAIA en WL) ===== */}
        {showBrandPanel && renderBrandPanel()}
      </Grid>
      <Box display="none">{/* preserved for layout parity */}</Box>
    </>
  );
};

export default Login;
